import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/tooltip";
import type { DailyDeliveryDayMetrics, DailyDeliveryVariantMetrics } from "@/pages/experiments/daily-delivery-metrics";
import { formatChartDayLabel } from "@/pages/experiments/diagnostics";
import { useState } from "react";

type DailyDeliverySparkProps = {
  days: DailyDeliveryDayMetrics[];
  mode: "global" | "custom";
  size?: "compact" | "expanded";
  showAxes?: boolean;
};

type VariantLegendItem = {
  key: string;
  variant: string;
  keyId: number;
  color: string;
};

type VariantSpendRow = {
  key: string;
  variant: string;
  keyId: number;
  allocationPct: number;
  actualSpend: number;
  targetSpend: number;
  spendSharePct: number;
  allocationDeltaPct: number;
  targetDeltaPct: number | null;
};

type GlobalSpendSummary = {
  rows: VariantSpendRow[];
  totalActualSpend: number;
  totalTargetSpend: number;
  totalAttainmentPct: number | null;
  totalDeltaPct: number | null;
};

type RelativeSpendSummary = {
  rows: VariantSpendRow[];
  totalActualSpend: number;
};

type SpendBarProps = {
  day: DailyDeliveryDayMetrics;
  maxVariantSpend: number;
  minHeightPct: number;
  colorMap: Map<string, string>;
  variantOrder: string[];
  showTargets: boolean;
};

type SpendBarSegment = {
  key: string;
  targetSpend: number;
  actualSpend: number;
  targetHeightPct: number;
  actualHeightPct: number;
  color: string;
};

const VARIANT_COLORS = [
  "hsl(var(--experiment-variant-1))",
  "hsl(var(--experiment-variant-2))",
  "hsl(var(--experiment-variant-3))",
  "hsl(var(--experiment-variant-4))",
  "hsl(var(--experiment-variant-5))",
  "hsl(var(--experiment-variant-6))",
  "hsl(var(--experiment-variant-7))",
  "hsl(var(--experiment-variant-8))",
];

const EXPANDED_DAY_SLOT_WIDTH_PX = 72;
const EXPANDED_MIN_WIDTH_PX = 240;
const EXPANDED_MAX_WIDTH_PX = 880;

function fmt(value: number): string {
  if (!Number.isFinite(value)) return "0";
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function fmtPct(value: number): string {
  if (!Number.isFinite(value)) return "0%";
  return `${value.toFixed(2)}%`;
}

function fmtSignedPct(value: number): string {
  if (!Number.isFinite(value)) return "0.00%";
  const abs = Math.abs(value).toFixed(2);
  if (value > 0) return `+${abs}%`;
  if (value < 0) return `-${abs}%`;
  return "0.00%";
}

function fmtCurrency(value: number): string {
  if (!Number.isFinite(value)) return "EUR 0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

function fmtAxisCurrency(value: number): string {
  if (!Number.isFinite(value)) return "EUR 0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    notation: "compact",
    minimumFractionDigits: 0,
    maximumFractionDigits: value >= 1000 ? 1 : 0,
  }).format(value);
}

function buildChartAxisMax(value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return 1;
  }

  const roughStep = value / 4;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalizedStep = roughStep / magnitude;

  let step = magnitude;
  if (normalizedStep > 1 && normalizedStep <= 2) {
    step = 2 * magnitude;
  } else if (normalizedStep > 2 && normalizedStep <= 2.5) {
    step = 2.5 * magnitude;
  } else if (normalizedStep > 2.5 && normalizedStep <= 5) {
    step = 5 * magnitude;
  } else if (normalizedStep > 5) {
    step = 10 * magnitude;
  }

  return step * 4;
}

function buildYAxisTicks(axisMax: number): number[] {
  return Array.from({ length: 5 }, (_, index) => axisMax - (axisMax / 4) * index);
}

function variantKey(variant: Pick<DailyDeliveryVariantMetrics, "variant" | "keyId">): string {
  return `${variant.variant}:${variant.keyId}`;
}

function buildVariantLegend(days: DailyDeliveryDayMetrics[]): VariantLegendItem[] {
  const seen = new Set<string>();
  const legend: VariantLegendItem[] = [];

  for (const day of days) {
    for (const variant of day.variants) {
      const key = variantKey(variant);
      if (seen.has(key)) continue;

      seen.add(key);
      legend.push({
        key,
        variant: variant.variant,
        keyId: variant.keyId,
        color: VARIANT_COLORS[legend.length % VARIANT_COLORS.length],
      });
    }
  }

  return legend;
}

function formatDateRange(days: DailyDeliveryDayMetrics[]): string {
  if (days.length === 0) return "";
  const first = formatChartDayLabel(days[0].date);
  const last = formatChartDayLabel(days[days.length - 1].date);
  return `${first} - ${last}`;
}

function buildVariantSpendRows(day: DailyDeliveryDayMetrics): VariantSpendRow[] {
  return day.variants
    .map(variant => {
      const actualSpend = variant.revenue;
      const targetSpend = variant.targetSpend;
      const spendSharePct = day.totalActualSpend > 0 ? (actualSpend / day.totalActualSpend) * 100 : 0;

      return {
        key: variantKey(variant),
        variant: variant.variant,
        keyId: variant.keyId,
        allocationPct: variant.trafficAllocationPct,
        actualSpend,
        targetSpend,
        spendSharePct,
        allocationDeltaPct: spendSharePct - variant.trafficAllocationPct,
        targetDeltaPct: targetSpend > 0 ? ((actualSpend - targetSpend) / targetSpend) * 100 : null,
      };
    })
    .sort((a, b) => Math.max(b.actualSpend, b.targetSpend) - Math.max(a.actualSpend, a.targetSpend));
}

function buildGlobalSpendSummary(day: DailyDeliveryDayMetrics): GlobalSpendSummary {
  const rows = buildVariantSpendRows(day);
  const totalActualSpend = rows.reduce((sum, row) => sum + row.actualSpend, 0);
  const totalTargetSpend = rows.reduce((sum, row) => sum + row.targetSpend, 0);

  return {
    rows,
    totalActualSpend,
    totalTargetSpend,
    totalAttainmentPct: totalTargetSpend > 0 ? (totalActualSpend / totalTargetSpend) * 100 : null,
    totalDeltaPct: totalTargetSpend > 0 ? ((totalActualSpend - totalTargetSpend) / totalTargetSpend) * 100 : null,
  };
}

function buildRelativeSpendSummary(day: DailyDeliveryDayMetrics): RelativeSpendSummary {
  const rows = buildVariantSpendRows(day).sort((a, b) => b.actualSpend - a.actualSpend);

  return {
    rows,
    totalActualSpend: rows.reduce((sum, row) => sum + row.actualSpend, 0),
  };
}

function buildSpendBarSegments({
  day,
  maxVariantSpend,
  minHeightPct,
  colorMap,
  variantOrder,
}: SpendBarProps): SpendBarSegment[] {
  const variantsByKey = new Map(day.variants.map(variant => [variantKey(variant), variant]));
  const orderedVariantKeys = variantOrder.length > 0 ? variantOrder : day.variants.map(variant => variantKey(variant));

  return orderedVariantKeys.map(key => {
    const variant = variantsByKey.get(key);
    const actualSpend = variant?.revenue || 0;
    const targetSpend = variant?.targetSpend || 0;

    return {
      key,
      targetSpend,
      actualSpend,
      targetHeightPct: targetSpend > 0 ? Math.max(minHeightPct, (targetSpend / maxVariantSpend) * 100) : 0,
      actualHeightPct: actualSpend > 0 ? Math.max(minHeightPct, (actualSpend / maxVariantSpend) * 100) : 0,
      color: colorMap.get(key) || VARIANT_COLORS[0],
    };
  });
}

function GroupedSpendDayBar({
  day,
  maxVariantSpend,
  minHeightPct,
  colorMap,
  variantOrder,
  showTargets,
}: SpendBarProps) {
  const hasSpend = day.variants.some(variant => variant.revenue > 0 || (showTargets && variant.targetSpend > 0));
  const segments = buildSpendBarSegments({
    day,
    maxVariantSpend,
    minHeightPct,
    colorMap,
    variantOrder,
    showTargets,
  });

  return (
    <div className="w-full h-full flex items-end">
      {!hasSpend ? (
        <div className="h-[2px] w-full rounded-[1px] bg-muted/70" />
      ) : (
        <div className="h-full w-full flex items-end gap-[1px]">
          {segments.map(segment => (
            <div key={`${day.date}-${segment.key}`} className="relative flex-1 h-full flex items-end justify-center">
              {showTargets && segment.targetSpend > 0 ? (
                <div
                  className="absolute bottom-0 w-full rounded-[2px]"
                  style={{
                    height: `${segment.targetHeightPct}%`,
                    backgroundColor: segment.color,
                    opacity: 0.18,
                  }}
                />
              ) : null}
              {segment.actualSpend > 0 ? (
                <div
                  className="absolute bottom-0 rounded-[2px]"
                  style={{
                    height: `${segment.actualHeightPct}%`,
                    width: showTargets ? "42%" : "100%",
                    backgroundColor: segment.color,
                  }}
                />
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DailyDeliverySpark({
  days,
  mode,
  size = "compact",
  showAxes = false,
}: DailyDeliverySparkProps) {
  if (days.length === 0) return null;

  const showTargets = mode === "global";
  const maxVariantSpend = Math.max(
    ...days.flatMap(day =>
      day.variants.flatMap(variant => (showTargets ? [variant.revenue, variant.targetSpend] : [variant.revenue])),
    ),
    1,
  );
  const chartAxisMax = size === "expanded" && showAxes ? buildChartAxisMax(maxVariantSpend) : maxVariantSpend;
  const yAxisTicks = size === "expanded" && showAxes ? buildYAxisTicks(chartAxisMax) : [];
  const sparkWidthClass = "w-full";
  const sparkHeightClass = size === "expanded" ? "h-[280px]" : "h-10";
  const sparkContainerClass = "w-full";
  const sparkGapClass = size === "expanded" ? "gap-2" : "gap-0";
  const sparkStyle =
    size === "expanded"
      ? {
          maxWidth: `${Math.min(Math.max(days.length * EXPANDED_DAY_SLOT_WIDTH_PX, EXPANDED_MIN_WIDTH_PX), EXPANDED_MAX_WIDTH_PX)}px`,
        }
      : undefined;
  const variantLegend = buildVariantLegend(days);
  const colorMap = new Map<string, string>(variantLegend.map(item => [item.key, item.color]));
  const variantOrder = variantLegend.map(item => item.key);
  const [hoveredDate, setHoveredDate] = useState(days[days.length - 1]?.date || days[0].date);
  const renderTooltipContent = (day: DailyDeliveryDayMetrics) => {
    const globalSpendSummary = buildGlobalSpendSummary(day);
    const relativeSpendSummary = buildRelativeSpendSummary(day);
    const attainmentInTarget =
      globalSpendSummary.totalDeltaPct != null && Math.abs(globalSpendSummary.totalDeltaPct) < 5;

    return (
      <TooltipContent side="top" sideOffset={8} align="center" className="w-[460px] p-3 pointer-events-none">
        <div className="space-y-3">
          <div>
            <div className="text-xs font-semibold">
              {showTargets ? "Daily spend by variant" : "Relative variant spend"}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {formatChartDayLabel(day.date)} | Window: {formatDateRange(days)}
            </div>
          </div>

          <div className="text-[10px] text-muted-foreground">
            {showTargets
              ? "Wide translucent bars show target spend. Narrow solid bars show actual spend."
              : "Bars compare actual spend across variants for each day."}
          </div>

          <div className="h-44">
            <div className="grid h-full grid-cols-7 gap-2">
              {days.map(chartDay => {
                const isActiveDay = chartDay.date === day.date;
                return (
                  <div key={chartDay.date} className="flex h-full flex-col items-center gap-1">
                    <div className={`w-full flex-1 rounded-[2px] ${isActiveDay ? "ring-1 ring-primary/80" : ""}`}>
                      <GroupedSpendDayBar
                        day={chartDay}
                        maxVariantSpend={chartAxisMax}
                        minHeightPct={14}
                        colorMap={colorMap}
                        variantOrder={variantOrder}
                        showTargets={showTargets}
                      />
                    </div>
                    <div
                      className={`text-[10px] ${
                        isActiveDay
                          ? "bg-muted/80 px-1 font-semibold text-foreground rounded-[2px]"
                          : "text-muted-foreground"
                      }`}
                    >
                      {formatChartDayLabel(chartDay.date)}
                    </div>
                    <div
                      className={`text-[10px] ${
                        isActiveDay ? "bg-muted/80 px-1 font-semibold text-foreground rounded-[2px]" : ""
                      }`}
                    >
                      A {fmt(chartDay.totalActualSpend)}
                    </div>
                    {showTargets ? (
                      <div className="text-[10px] text-muted-foreground">T {fmt(chartDay.totalTargetSpend)}</div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          {showTargets ? (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-[11px]">
                <span className="font-medium">Actual vs target spend</span>
                <span className="text-muted-foreground">
                  {fmtCurrency(globalSpendSummary.totalActualSpend)} /{" "}
                  {fmtCurrency(globalSpendSummary.totalTargetSpend)}
                </span>
              </div>
              <div className="text-[11px]">
                <span className="text-muted-foreground">Target attainment:</span>{" "}
                {globalSpendSummary.totalAttainmentPct == null ? (
                  <span>N/A</span>
                ) : (
                  <span className={attainmentInTarget ? "text-emerald-600" : "text-rose-600"}>
                    {fmtPct(globalSpendSummary.totalAttainmentPct)}
                  </span>
                )}
              </div>

              <div className="rounded border border-border/70">
                <div className="grid grid-cols-[1.2fr,0.5fr,0.75fr,0.75fr,0.7fr] gap-x-2 border-b border-border/60 px-2 py-1 text-[10px] font-medium text-muted-foreground">
                  <span>Variant</span>
                  <span>Alloc</span>
                  <span>Target</span>
                  <span>Actual</span>
                  <span>Delta</span>
                </div>
                {globalSpendSummary.rows.map(row => (
                  <div
                    key={row.key}
                    className="grid grid-cols-[1.2fr,0.5fr,0.75fr,0.75fr,0.7fr] gap-x-2 px-2 py-1 text-[10px] border-b border-border/40 last:border-b-0"
                  >
                    <span className="truncate">
                      <span
                        className="inline-block h-2 w-2 rounded-[2px] mr-1 align-middle"
                        style={{ backgroundColor: colorMap.get(row.key) || VARIANT_COLORS[0] }}
                        aria-hidden="true"
                      />
                      {row.variant} <span className="text-muted-foreground">ID {row.keyId}</span>
                    </span>
                    <span>{fmtPct(row.allocationPct)}</span>
                    <span>{fmt(row.targetSpend)}</span>
                    <span>{fmt(row.actualSpend)}</span>
                    <span
                      className={
                        row.targetDeltaPct != null && Math.abs(row.targetDeltaPct) < 5
                          ? "text-emerald-600"
                          : "text-rose-600"
                      }
                    >
                      {row.targetDeltaPct == null ? "—" : fmtSignedPct(row.targetDeltaPct)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-[11px]">
                <span className="font-medium">Variant spend comparison</span>
                <span className="text-muted-foreground">{fmtCurrency(relativeSpendSummary.totalActualSpend)}</span>
              </div>

              <div className="rounded border border-border/70">
                <div className="grid grid-cols-[1.2fr,0.5fr,0.75fr,0.75fr,0.7fr] gap-x-2 border-b border-border/60 px-2 py-1 text-[10px] font-medium text-muted-foreground">
                  <span>Variant</span>
                  <span>Alloc</span>
                  <span>Actual</span>
                  <span>Share</span>
                  <span>Delta</span>
                </div>
                {relativeSpendSummary.rows.map(row => (
                  <div
                    key={row.key}
                    className="grid grid-cols-[1.2fr,0.5fr,0.75fr,0.75fr,0.7fr] gap-x-2 px-2 py-1 text-[10px] border-b border-border/40 last:border-b-0"
                  >
                    <span className="truncate">
                      <span
                        className="inline-block h-2 w-2 rounded-[2px] mr-1 align-middle"
                        style={{ backgroundColor: colorMap.get(row.key) || VARIANT_COLORS[0] }}
                        aria-hidden="true"
                      />
                      {row.variant} <span className="text-muted-foreground">ID {row.keyId}</span>
                    </span>
                    <span>{fmtPct(row.allocationPct)}</span>
                    <span>{fmt(row.actualSpend)}</span>
                    <span>{fmtPct(row.spendSharePct)}</span>
                    <span className={Math.abs(row.allocationDeltaPct) < 5 ? "text-emerald-600" : "text-rose-600"}>
                      {fmtSignedPct(row.allocationDeltaPct)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
            {variantLegend.map(variant => (
              <div key={variant.key} className="flex items-center gap-2 text-[11px]">
                <span
                  className="h-2.5 w-2.5 rounded-[2px]"
                  style={{ backgroundColor: variant.color }}
                  aria-hidden="true"
                />
                <span className="truncate">
                  {variant.variant} <span className="text-muted-foreground">ID {variant.keyId}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </TooltipContent>
    );
  };

  const renderDayTrigger = (day: DailyDeliveryDayMetrics) => (
    <Tooltip key={day.date}>
      <TooltipTrigger asChild>
        <div
          className={
            size === "expanded"
              ? "flex-1 h-full min-w-0 cursor-default"
              : "flex-1 h-full cursor-default px-[1px] first:pl-0 last:pr-0"
          }
          onMouseEnter={() => setHoveredDate(day.date)}
          onMouseMove={() => setHoveredDate(day.date)}
        >
          <GroupedSpendDayBar
            day={day}
            maxVariantSpend={chartAxisMax}
            minHeightPct={10}
            colorMap={colorMap}
            variantOrder={variantOrder}
            showTargets={showTargets}
          />
        </div>
      </TooltipTrigger>
      {renderTooltipContent(day)}
    </Tooltip>
  );

  return (
    <TooltipProvider delayDuration={40} skipDelayDuration={200} disableHoverableContent>
      <div className={sparkContainerClass}>
        {size === "expanded" && showAxes ? (
          <div
            className={`grid ${sparkHeightClass} w-full grid-cols-[56px,minmax(0,1fr)] grid-rows-[minmax(0,1fr)_28px]`}
          >
            <div className="flex h-full flex-col justify-between pr-2 text-right text-[11px] text-muted-foreground">
              {yAxisTicks.map((tick, index) => (
                <div
                  key={tick}
                  className={`leading-none ${index === 0 ? "-translate-y-1/2" : index === yAxisTicks.length - 1 ? "translate-y-1/2" : ""}`}
                >
                  {fmtAxisCurrency(tick)}
                </div>
              ))}
            </div>
            <div className="relative h-full border-b border-l border-border/70">
              <div className="pointer-events-none absolute inset-0 flex h-full flex-col justify-between">
                {yAxisTicks.slice(0, -1).map(tick => (
                  <div key={tick} className="w-full border-t border-border/30" />
                ))}
              </div>
              <div className={`mx-auto flex h-full items-end ${sparkGapClass} ${sparkWidthClass}`} style={sparkStyle}>
                {days.map(day => renderDayTrigger(day))}
              </div>
            </div>
            <div />
            <div className="w-full">
              <div className={`mx-auto flex h-full ${sparkGapClass} ${sparkWidthClass}`} style={sparkStyle}>
                {days.map(day => {
                  const isHoveredDay = day.date === hoveredDate;
                  return (
                    <div
                      key={`label-${day.date}`}
                      className={`flex-1 min-w-0 truncate text-center text-[11px] ${
                        isHoveredDay ? "font-medium text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {formatChartDayLabel(day.date)}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div
            className={`mx-auto flex ${sparkHeightClass} items-end ${sparkGapClass} ${sparkWidthClass}`}
            style={sparkStyle}
          >
            {days.map(day => renderDayTrigger(day))}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
